/**
 * 소셜 로그인 버튼 컴포넌트 (모바일)
 * Naver, Apple OAuth 로그인 버튼 그룹
 * Capacitor 네이티브 환경에서는 Browser.open() + 딥링크 사용
 * PWA/웹 환경에서는 기존 window.location 방식 사용
 *
 * 아이콘 출처:
 * - Naver: https://developers.naver.com/docs/login/bi/
 * - Apple: https://developer.apple.com/design/human-interface-guidelines/sign-in-with-apple
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { CapacitorNaverLogin } from "@team-lepisode/capacitor-naver-login";
import { createLogger } from "@ict/shared";
import { DEEPLINK_SCHEME } from "@/lib/constants/app";
import { setAuthToken } from "@/lib/apollo";

const logger = createLogger({ module: "SocialLoginButtons" });

// OAuth 제공자 타입
type OAuthProvider = "naver" | "apple";

// 네이버 아이콘 (공식 N 로고, 여백 추가로 다른 아이콘과 균형 맞춤)
function NaverIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="-3 -3 30 30"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M16.273 12.845L7.376 0H0v24h7.727V11.155L16.624 24H24V0h-7.727v12.845z" />
    </svg>
  );
}

// Apple 아이콘 (공식 Sign in with Apple 가이드라인)
function AppleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

// 제공자별 설정
const PROVIDERS: {
  id: OAuthProvider;
  name: string;
  bgColor: string;
  textColor: string;
  iconColor: string;
  Icon: React.FC<{ className?: string }>;
}[] = [
  {
    id: "naver",
    name: "네이버",
    bgColor: "bg-[#03C75A]",
    textColor: "text-white",
    iconColor: "text-white",
    Icon: NaverIcon,
  },
  {
    id: "apple",
    name: "Apple",
    bgColor: "bg-black",
    textColor: "text-white",
    iconColor: "text-white",
    Icon: AppleIcon,
  },
];

// API 서버 URL
const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:23998";

// Capacitor 네이티브 환경인지 확인
const isNativePlatform = () => Capacitor.isNativePlatform();

interface SocialLoginButtonsProps {
  /** 활성화할 제공자 목록 (기본: naver, apple) */
  enabledProviders?: OAuthProvider[];
}

export function SocialLoginButtons({
  enabledProviders = ["naver", "apple"],
}: SocialLoginButtonsProps) {
  const [loadingProvider, setLoadingProvider] = useState<OAuthProvider | null>(null);
  const navigate = useNavigate();

  // Android에서는 Apple 로그인 제외
  const platform = Capacitor.getPlatform();
  const filteredProviders =
    platform === "android" ? enabledProviders.filter((p) => p !== "apple") : enabledProviders;

  // 네이버 네이티브 SDK 로그인
  const handleNaverNativeLogin = async () => {
    setLoadingProvider("naver");
    try {
      // 1. Naver SDK 로그인 (네이버 앱 또는 인앱 브라우저)
      const result = await CapacitorNaverLogin.login();

      if (!result.accessToken) {
        throw new Error("accessToken을 받지 못했습니다");
      }

      // 2. 백엔드에 accessToken 전송하여 JWT 발급
      const response = await fetch(`${API_BASE_URL}/oauth/naver/native`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessToken: result.accessToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "로그인 실패");
      }

      if (data.token) {
        // 기존 회원: JWT 저장 후 프로젝트 목록으로 이동
        setAuthToken(data.token);
        navigate("/projects", { replace: true });
      } else if (data.pendingSession) {
        // 신규 회원: sessionToken과 함께 프로필 완성 페이지로
        navigate(`/complete-profile?session=${data.sessionToken}`, { replace: true });
      }
    } catch (error) {
      logger.error("네이버 네이티브 로그인 실패:", error);
      // SDK 실패 시 기존 웹 OAuth 폴백
      await handleWebOAuthLogin("naver");
    } finally {
      setLoadingProvider(null);
    }
  };

  // OAuth 로그인 시작 (분기 처리)
  const handleOAuthLogin = async (provider: OAuthProvider) => {
    // 네이티브 + 네이버: SDK 사용
    if (provider === "naver" && isNativePlatform()) {
      await handleNaverNativeLogin();
      return;
    }
    // 그 외: 기존 웹 OAuth (Browser.open)
    await handleWebOAuthLogin(provider);
  };

  // 웹 OAuth 로그인 (Browser.open 또는 페이지 리다이렉트)
  const handleWebOAuthLogin = async (provider: OAuthProvider) => {
    setLoadingProvider(provider);

    // returnUrl 결정: 네이티브 환경은 딥링크, 웹 환경은 웹 URL
    let returnUrl: string;

    if (isNativePlatform()) {
      // Capacitor 네이티브 환경: 딥링크 URL 사용
      returnUrl = `${DEEPLINK_SCHEME}://oauth/callback`;
    } else {
      // PWA/웹 환경: 현재 origin 기반 URL 사용
      const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
      returnUrl = `${baseUrl}/oauth/callback`;
    }

    // OAuth authorize URL 생성
    const params = new URLSearchParams();
    params.set("returnUrl", returnUrl);

    const authUrl = `${API_BASE_URL}/oauth/${provider}/authorize?${params.toString()}`;

    if (isNativePlatform()) {
      // Capacitor 네이티브 환경: In-App Browser 사용
      // 인증 완료 후 딥링크로 앱으로 돌아옴
      try {
        await Browser.open({
          url: authUrl,
          presentationStyle: "popover", // iOS에서 모달 형태로 열기
        });
      } catch (error) {
        logger.error("브라우저 열기 실패:", error);
        setLoadingProvider(null);
      }
    } else {
      // PWA/웹 환경: 기존 방식 (페이지 리다이렉트)
      window.location.href = authUrl;
    }
  };

  // 활성화된 제공자만 필터링 (Android에서 Apple 제외)
  const activeProviders = PROVIDERS.filter((p) => filteredProviders.includes(p.id));

  return (
    <div className="flex flex-col gap-3">
      {activeProviders.map((provider) => {
        const Icon = provider.Icon;
        return (
          <button
            key={provider.id}
            type="button"
            onClick={() => handleOAuthLogin(provider.id)}
            disabled={loadingProvider !== null}
            className={`
              flex items-center justify-center gap-2 w-full py-3 rounded-lg
              font-medium transition-all active:scale-[0.98]
              disabled:opacity-50 disabled:cursor-not-allowed
              ${provider.bgColor} ${provider.textColor}
            `}
            aria-label={`${provider.name}로 로그인`}
          >
            <Icon className={`w-5 h-5 ${provider.iconColor}`} />
            <span>
              {loadingProvider === provider.id ? "연결 중..." : `${provider.name}로 로그인`}
            </span>
          </button>
        );
      })}
    </div>
  );
}
